/**
 * GAME NIGHT — the phone page (3039; DESIGN-CHANGELOG 2026-08-25 and the
 * entries above it). Port of `design/GT Mobile Round.dc.html`, whose copy
 * still says ROUND: Marcus renamed the feature in the app on 2026-08-28 and
 * the prototype owes the same edit upstream (repo CLAUDE.md). Everything
 * under the copy — files, screen key, RPCs, tables — is still `round`.
 *
 * One column: wheel → pin card → controls (leader only) → WHO'S IN →
 * [+ NOMINATE] → GAME NIGHT CONTROLS (leader / owner). The browser is a bottom
 * sheet (`sheets/NominateSheet.tsx`), not a section. Full screen: the shell
 * hides its header and tab bar while this is up (`headerVM().hdrOn`).
 *
 * Every value is `roundVM(s, s.serverNow())`; `useRoundClock` re-renders per
 * frame while the wheel moves, so the pose is always the shared clock's.
 */

import { Avatar } from '../components/Avatar.tsx';
import { CoverArt } from '../components/CoverArt.tsx';
import { PixelButton } from '../components/PixelButton.tsx';
import { QuarterIcon } from '../components/QuarterIcon.tsx';
import { useArt, useQueueArt } from '../components/useArt.ts';
import { ArmedLabel, hueSplit, LeaderCrown, NominatorStack, QuarterSocket, useRoundClock, Wheel, WheelPointer } from '../components/Wheel.tsx';
import { useQd } from '../store/index.ts';
import { roundVM } from '../store/selectors.round.ts';
import type { ArmPairVM, PipVM, RoundVM } from '../store/selectors.round.ts';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

/** The three coin slots — a coin for a quarter still held, a socket where one went. */
export function Pips({ pips, size }: { pips: PipVM[]; size: number }): JSX.Element {
  const u = size / 14;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, height: size, flex: 'none' }}>
      {pips.map((q) =>
        q.on ? (
          <QuarterIcon key={q.key} hover={false} u={u} sparkles={false} auto={false} />
        ) : (
          <QuarterSocket key={q.key} size={size} />
        ),
      )}
    </div>
  );
}

/** A 44/48px armed control (LEAVE / UNLOCK / UNDO / RESET / END). */
function Armed({
  rest,
  pair,
  arm,
  h,
  onClick,
  style,
  tip,
}: {
  rest: string;
  pair: ArmPairVM;
  arm: string;
  h: number;
  onClick: () => void;
  style?: CSSProperties;
  tip?: string;
}): JSX.Element {
  return (
    <div
      onClick={onClick}
      data-arm={arm}
      data-tip={tip || undefined}
      style={{
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: h,
        padding: '0 11px',
        border: '1px solid ' + pair.bd,
        background: pair.bg,
        color: pair.clr,
        fontFamily: MONO,
        fontSize: '9.5px',
        letterSpacing: '.05em',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...style,
      }}
    >
      <ArmedLabel rest={rest} restV={pair.restV} armV={pair.armV} fill={pair.fill} anim={pair.anim} />
    </div>
  );
}

function PinCard({ v }: { v: RoundVM }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const art = useArt(v.cardGoId + 'p', v.cardCands);
  return (
    <div
      onClick={v.cardGoId ? () => openGame(v.cardGoId) : undefined}
      style={{
        cursor: v.cardCur,
        display: 'flex',
        gap: 12,
        alignItems: 'stretch',
        border: v.cardBw + ' solid ' + v.cardBd,
        ...hueSplit(v.cardHues),
        background: 'var(--gt-tile)',
        padding: 11,
      }}
    >
      <div style={{ position: 'relative', width: 74, flex: 'none', aspectRatio: '2/3', border: '1px solid var(--gt-line)', background: v.cardOn ? v.cardFall : undefined }}>
        {v.cardOn ? (
          <CoverArt style={{ position: 'absolute', inset: 0 }} fs={14} ini={v.cardIni} hasCover={art.hasCover} cover={art.cover} />
        ) : (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gt-field)' }}>
            <span style={{ fontFamily: PIXEL, fontSize: 24, color: 'var(--gt-ghost)', transform: 'translateY(0.13em)' }}>?</span>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, justifyContent: 'flex-start', minWidth: 0, flex: 1 }}>
        <span style={{ fontFamily: 'var(--gt-font-sans)', fontSize: 17, fontWeight: 600, color: v.cardNmClr, lineHeight: 1.3, textWrap: 'pretty' } as CSSProperties}>
          {v.cardNm}
        </span>
        {/* every backer (3054), then what their quarters bought */}
        {v.cardByOn ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <NominatorStack bys={v.cardBys} size={24} fs={8} />
            <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '.06em', color: 'var(--gt-text-4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {v.cardByTxt}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function Round(): JSX.Element | null {
  const s = useQd();
  const v = roundVM(s, s.serverNow());
  useRoundClock(s.round?.spin ?? null, s.serverNow);

  // The wheel, the rail and the pin card all paint covers, and a round's pool
  // is not the Discover head or the catalog head — the two sets
  // `revalidateVisible` covers — so nothing else asks `/api/steam/items` about
  // a nominated game (the note in `components/useArt.ts`).
  //
  // BEFORE the `if (!v)` below, like `useRoundClock` above it: a hook may not
  // sit after an early return. An idle round is `null` and queues nothing.
  useQueueArt(v ? [...v.wedges.map((w) => w.nom.id), ...v.tiles.map((t) => t.id), v.cardGoId] : []);

  if (!v) return null;

  const box44: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 44,
    fontFamily: MONO,
    letterSpacing: '.06em',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
  };

  return (
    <div
      data-screen-label="Game night"
      // No `minHeight:100vh` of its own: the frame's column already carries it,
      // and a second one inside a flex child added the Android toolbar band to
      // the page — a screen that scrolled by nothing (Marcus, staging
      // 2026-08-26). `overflowX:hidden` so nothing on the page can ever hand
      // the column a sideways scroll.
      style={{ userSelect: 'none', WebkitUserSelect: 'none', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowX: 'hidden', background: 'var(--gt-bg)' }}
    >
      {/* top bar --------------------------------------------------------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: '1px solid var(--gt-line)', background: 'var(--gt-panel)', position: 'sticky', top: 0, zIndex: 20 }}>
        <div onClick={() => s.rBack()} style={{ ...box44, padding: '0 11px', border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-4)', fontSize: '9.5px', letterSpacing: '.05em', flex: 'none' }}>
          {v.backTxtM}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
          <span style={{ fontFamily: PIXEL, fontSize: '9.5px', color: 'var(--gt-text)', transform: 'translateY(0.13em)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            GAME NIGHT
          </span>
          <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', color: 'var(--gt-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {v.rHeadTxt}
          </span>
        </div>
        {v.rIn ? <Armed rest="✕ LEAVE" pair={v.lv} arm="leave" h={44} onClick={() => void s.rLeave()} style={{ flex: 'none' }} /> : null}
      </div>

      {/* not in ---------------------------------------------------------- */}
      {v.rOut ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--acc2Soft)', borderBottom: '1px solid var(--gt-line)', padding: '13px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontFamily: MONO, fontSize: 11, lineHeight: 1, color: 'var(--acc2)' }}>✕</span>
            <span style={{ fontFamily: PIXEL, fontSize: 8, color: 'var(--acc2)', transform: 'translateY(0.13em)' }}>YOU&apos;RE NOT IN GAME NIGHT</span>
          </div>
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', lineHeight: 1.8, color: 'var(--gt-text-5)' }}>{v.rOutTxt}</span>
          {v.rCanJoin ? <PixelButton label="JOIN GAME NIGHT ▸" fs={10} pad="15px 16px" center busy={s.rBusy} onClick={() => void s.rJoin()} /> : null}
        </div>
      ) : null}

      {/* wheel · pin card · leader controls ------------------------------- */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 14 }}>
        <div style={{ position: 'relative', width: 320, height: 328, margin: '0 auto' }}>
          <div style={{ position: 'absolute', left: 0, top: 8 }}>
            <Wheel wedges={v.wedges} rot={v.rot} spin={s.round?.spin ?? null} serverNow={s.serverNow} px={320} winId={v.winId} winHue={v.winHue} keyPrefix="m" onOpen={(id) => s.openGame(id)} />
          </div>
          <div style={{ position: 'absolute', left: '50%', top: 168, marginLeft: -14, marginTop: -14, zIndex: 3 }}>
            <QuarterIcon hover={false} u={2} sparkles={false} />
          </div>
          <WheelPointer style={{ position: 'absolute', left: '50%', top: 0, marginLeft: -13, zIndex: 4 }} />
          {v.wheelEmpty ? (
            <div style={{ position: 'absolute', left: '50%', top: 168, transform: 'translate(-50%,24px)', zIndex: 5, width: 210, border: '1px solid var(--gt-border-2)', background: 'var(--gt-ink)', padding: '10px 12px', textAlign: 'center', fontFamily: MONO, fontSize: 9, letterSpacing: '.08em', lineHeight: 1.9, color: 'var(--gt-muted)' }}>
              {v.emptyTxt}
            </div>
          ) : null}
        </div>

        <PinCard v={v} />

        {v.rHostOn ? (
          <>
            <div
              onClick={v.wtCur === 'pointer' ? () => void s.rTglWt() : undefined}
              data-tip={v.wtTip || undefined}
              style={{ ...box44, opacity: v.wtOp, pointerEvents: v.wtPE, border: '1px solid ' + v.wtBd, background: v.wtBg, color: v.wtClr, fontSize: 10, cursor: v.wtCur }}
            >
              {v.wtLbl}
            </div>
            {v.canSpin || v.stSpinning ? (
              <PixelButton label={v.spinLbl} fs={13} pad="20px 18px" center busy={v.stSpinning || s.rBusy} onClick={() => void s.rSpin()} />
            ) : null}
            {v.needNoms ? (
              <div style={{ border: '1px dashed var(--gt-border-3)', padding: 14, textAlign: 'center', fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', lineHeight: 1.9, color: 'var(--gt-muted-2)' }}>
                NOTHING TO SPIN YET — NOBODY HAS NOMINATED
              </div>
            ) : null}
            {v.stWin ? (
              <>
                <PixelButton label="LOCK IT IN ▸" fs={12} pad="18px 18px" center busy={s.rBusy} onClick={() => void s.rLockIn()} />
                <div onClick={() => void s.rSpin()} style={{ ...box44, border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-5)', fontSize: '9.5px' }}>
                  ⟳ SPIN AGAIN — UNTIL YOU LOCK
                </div>
              </>
            ) : null}
            {v.stLock ? (
              <>
                <div style={{ border: '1px solid var(--acc)', background: 'var(--accSoft)', padding: 14, fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.06em', lineHeight: 1.8, color: 'var(--acc)' }}>
                  ✓ LOCKED — TONIGHT: {v.winNm}
                </div>
                <Armed rest="⟳ UNLOCK — PUT IT BACK IN PLAY" pair={v.un} arm="unlock" h={44} onClick={() => void s.rUnlock()} />
              </>
            ) : null}
          </>
        ) : null}
      </div>

      {/* WHO'S IN --------------------------------------------------------- */}
      <div style={{ display: 'flex', flexDirection: 'column', borderTop: '1px solid var(--gt-line)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--gt-raised)', borderBottom: '1px solid var(--gt-border)', padding: '11px 14px' }}>
          <span style={{ fontFamily: PIXEL, fontSize: 8, color: 'var(--gt-text)', transform: 'translateY(0.13em)', whiteSpace: 'nowrap' }}>WHO&apos;S IN</span>
          <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 9, letterSpacing: '.08em', color: 'var(--gt-muted)', whiteSpace: 'nowrap' }}>{v.rosterTxt}</span>
        </div>
        {v.roster.map((p) => (
          <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--gt-line-0)' }}>
            <div style={{ position: 'relative', display: 'flex', flex: 'none' }}>
              {p.host ? <LeaderCrown style={{ position: 'absolute', left: '50%', top: -8, marginLeft: -8, zIndex: 3 }} /> : null}
              <Avatar size={26} fs={8} ini={p.ini} bg={p.bg} hasAv={p.hasAv} avBg={p.avBg} />
            </div>
            <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.05em', color: p.clr, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>{p.nm}</span>
            <Pips pips={p.pips} size={20} />
            {/* The leader's ✕ (2026-08-28). Armed like LEAVE, and the same
                grid-area trick keeps the row from reflowing when ✕ becomes
                SURE? — a roster that jumped under a thumb would be a mis-tap
                waiting to happen. */}
            {p.kickOn ? (
              <Armed rest="✕" pair={p.kick} arm={'kick:' + p.key} h={32} tip={p.kickTip} onClick={() => void s.rKick(p.key)} style={{ flex: 'none' }} />
            ) : null}
          </div>
        ))}
      </div>

      {/* + NOMINATE ------------------------------------------------------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 14, borderTop: '1px solid var(--gt-line)' }}>
        <div
          onClick={v.addCur === 'pointer' ? () => s.rSheetOpen() : undefined}
          style={{ ...box44, flex: 1, height: 48, border: '1px solid ' + v.addBd, background: v.addBg, color: v.addClr, fontSize: 10, cursor: v.addCur }}
        >
          {v.addLbl}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, height: 48, padding: '0 10px', border: '1px solid var(--gt-border-2)', background: 'var(--gt-field)', boxSizing: 'border-box', flex: 'none' }}>
          <Pips pips={v.pips} size={22} />
        </div>
      </div>

      {/* GAME NIGHT CONTROLS ---------------------------------------------- */}
      {v.rLifeOn ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14, borderTop: '1px solid var(--gt-line)' }}>
          <span style={{ fontFamily: PIXEL, fontSize: 8, color: 'var(--gt-text-4)', transform: 'translateY(0.13em)' }}>GAME NIGHT CONTROLS</span>
          <div style={{ display: 'flex', gap: 10 }}>
            {v.rUndoOn ? <Armed rest="⟳ UNDO SPIN" pair={v.ud} arm="undo" h={48} onClick={() => void s.rUndoSpin()} style={{ flex: 1, fontSize: '9.5px' }} /> : null}
            {v.rResetOn ? <Armed rest="✕ RESET" pair={v.rs} arm="reset" h={48} onClick={() => void s.rReset()} style={{ flex: 1, fontSize: '9.5px' }} /> : null}
            {v.rEndOn ? <Armed rest="▮ END NIGHT" pair={v.en} arm="end" h={48} onClick={() => void s.rEnd()} style={{ flex: 1, fontSize: '9.5px' }} /> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
